import { PublicKey } from '@solana/web3.js'

// API interactions
import { apiNewPostReceiveCctpMessage, apiSendOrderData } from '@src/api/order.api'
import useApproveDelegate from '@src/chains/solana/approveDelegate'
import useDepositForBurn from '@src/chains/solana/depositForBurn'
// Solana-specific hooks
import usePlaceOrder from '@src/chains/solana/placeOrder'
import { useUnWrapWSOL } from '@src/chains/solana/useUnWrapWSOL'
import { useWrapSOL } from '@src/chains/solana/useWrapSOL'
import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
// Enums
import { ExchangeType, ResultCode, StepNames } from '@src/enums'
// Custom hooks
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
// Types
import { ChainHandler, IExchangeData, SolanaSwapParams, UpdateData } from '@src/types/exchange'
import { IOrderDirectionParameter, IOrderFundingParameter } from '@src/types/order'
import { Address } from '@src/types/token'
import { isSolanaAddress } from '@src/utils'
import { addressToBytes32 } from '@src/utils/addressBytes32'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
// Utility functions
import { createContextLogger } from '@src/utils/logger'
import { processSolanaReceipt } from '@src/utils/processReceipt'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { useConfigStore } from '@src/zustand_stores'
// Add Zustand store
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useExchangeTokenSolana')

export function useExchangeTokenSolana(): ChainHandler {
    // Replace Redux selector with Zustand
    const referralCode = useApplicationStore(state => state.generatedReferralCode)
    const { setOrderId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const availableNetworks = useConfigStore(config => config.availableNetworks)

    // Remove dispatch
    // const dispatch = useAppDispatch()
    const { waitForTransactionReceipt } = useWaitForTransaction()
    const { placeOrder } = usePlaceOrder()
    const { depositForBurn } = useDepositForBurn()
    const { approveDelegate, waitForApproval } = useApproveDelegate()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { wrapSol, waitForWrap } = useWrapSOL()
    const { unwrapSol, waitForUnwrap } = useUnWrapWSOL()

    const handleApproval = async (
        updateData: UpdateData,
        sellData: IExchangeData,
        walletAddress: string
    ): Promise<string> => {
        try {
            if (!walletAddress) {
                throw new Error('No Solana wallet connected')
            }

            logger.info(
                'Handling Solana token approval. updateData=',
                updateData,
                'sellData=',
                sellData,
                'walletAddress=',
                walletAddress
            )

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS,
                result: { ...updateData.result, message: 'Please approve tokens.' },
            })

            const mappedTokenAddress =
                sellData.token.address === NATIVE_TOKEN_ADDRESS[ChainId.Solana]
                    ? WRAPPED_GAS_TOKENS[ChainId.Solana].address
                    : sellData.token.address

            const tokenMint = new PublicKey(mappedTokenAddress)
            const owner = new PublicKey(walletAddress)

            const hash = await approveDelegate(tokenMint, owner)

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS_PENDING,
                result: {
                    ...updateData.result,
                    message: 'Waiting for approval confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await new Promise(r => setTimeout(r, 100))

            await waitForApproval(hash)

            return hash
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the approval transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error('Solana approval failed:', error)
            throw error
        }
    }

    const handleCctpExchange = async (hash: string, exchangeId: string, sellData: IExchangeData) => {
        try {
            logger.info('Handling CCTP exchange', { hash })

            const result = await apiNewPostReceiveCctpMessage(availableNetworks[sellData.chainId].name, hash)

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Allow 5-20 minutes for matching.',
                    transactionETA: result?.eta || 20,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            return result
        } catch (error) {
            logger.error('CCTP exchange failed:', error)
            throw error
        }
    }

    const executeSwap = async (params: SolanaSwapParams, referrer?: string): Promise<string> => {
        const { sellData, buyData, quote, destinationAddress, exchangeId, liquiditySource, onSuccess } = params

        try {
            logger.info('Executing Solana swap', { params })

            updateHistory(exchangeId, {
                currentStep: StepNames.CONFIRM_SWAP,
                result: {
                    status: ResultCode.pending,
                    message: 'Please confirm swap.',
                },
            })

            let hash: string
            const exchangeType = liquiditySource === 'cctp_direct' ? ExchangeType.cctp : ExchangeType.order_book

            if (exchangeType === ExchangeType.cctp) {
                // Handle CCTP deposit for burn
                const destinationBytes32 = addressToBytes32(destinationAddress || '', buyData.chainId)
                hash = await depositForBurn(
                    sellData.value || BigInt(0),
                    destinationBytes32,
                    availableNetworks[buyData.chainId]?.cctp_id,
                    params.walletAddress,
                    true
                )
            } else {
                const orderDir: IOrderDirectionParameter = {
                    srcAsset: mapTokenAddress(
                        (quote?.order_data?.order_direction?.src_token_address as string) || sellData.token?.address,
                        true,
                        sellData.chainId
                    ),
                    dstAsset: mapTokenAddress(buyData.token?.address, true, buyData.chainId),
                    dstLzc: availableNetworks[buyData.chainId]?.lz_id,
                }

                const orderFunding: IOrderFundingParameter = {
                    srcQuantity: sellData.value || BigInt(0),
                    dstQuantity: buyData.value || BigInt(0),
                    bondAsset: quote?.order_data.order_funding.bond_token_address || '',
                    bondAmount: quote?.order_data.order_funding.bond_amount
                        ? BigInt(quote.order_data.order_funding.bond_amount)
                        : BigInt(0),
                    bondFee: Number(quote?.order_data.order_funding.bond_fee || 0),
                }

                hash = await placeOrder(orderDir, orderFunding, (destinationAddress || '') as Address)
            }

            if (!hash) {
                throw new Error('Could not send transaction.')
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.CONFIRM_SWAP_PENDING,
                result: {
                    status: ResultCode.pending,
                    message: 'Waiting for confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            let result
            if (exchangeType === ExchangeType.cctp) {
                result = await handleCctpExchange(hash, exchangeId, sellData)
            } else {
                await waitForTransactionReceipt(hash, true, false)

                updateHistory(exchangeId, {
                    currentStep: StepNames.MATCHING,
                    result: {
                        status: ResultCode.pending,
                        message: 'Order matching in progress.',
                        transactionETA: 60,
                        sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                    },
                })

                const decodedData = processSolanaReceipt(hash, availableNetworks[sellData.chainId].name)
                result = await apiSendOrderData(decodedData[0], referralCode.code, referrer)
            }

            if (result?.status === ResultCode.failure) {
                throw new Error('Order failed to process')
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Order matching in-progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            // Update Redux dispatch calls to Zustand
            setOrderId(result?.id?.toString() || '')

            // small delay to start timer, without instant Match Executed
            await new Promise(r => setTimeout(r, 250))

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_EXECUTED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order successfully sent.',
                    transactionETA: result?.eta || 10,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            onSuccess()
            return hash
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the swap transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error('Solana swap failed:', error)
            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    const handleWrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating wrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.DEPOSIT_WETH,
                result: { ...updateData.result, message: `Depositing ${amount} gas.` },
            })

            if (WRAPPED_GAS_TOKENS[sellData.chainId] && isSolanaAddress(WRAPPED_GAS_TOKENS[sellData.chainId].address)) {
                const hash = await wrapSol(BigInt(amount))

                updateHistory(null, {
                    ...updateData,
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                    result: {
                        ...updateData.result,
                        message: `Depositing ${amount} gas.`,
                        sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                    },
                })

                await waitForWrap(hash)
                logger.success('Wrap operation completed', { hash })

                return hash
            } else {
                logger.fatal(`No wrapped gas token contract found for chainId ${sellData.chainId}`)
                throw new Error(
                    `Error wrapping gas asset: No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
            }
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the wrap transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error('Wrap operation failed', error)
            throw new Error('Error wrapping gas asset. Please try again.')
        }
    }

    const handleUnwrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating unwrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH,
                result: { ...updateData.result, message: `Unwrapping ${amount} gas.` },
            })

            const hash = await unwrapSol(amount)
            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH_PENDING,
                result: {
                    ...updateData.result,
                    message: `Unwrapping ${amount} gas.`,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForUnwrap(hash)
            logger.info('Unwrap operation completed', { hash })

            return hash
        } catch (error) {
            logger.error('Unwrap operation failed', error)
            throw new Error('Error unwrapping gas asset. Please try again.')
        }
    }

    return {
        executeSwap,
        handleApproval,
        handleWrap,
        handleUnwrap,
    }
}
